import { AttributeSchema } from "@redotech/redo-inventory-common/attributes";
import { ProductFamilyKindSchema } from "@redotech/redo-inventory-common/general-listing/general-listing";
import { ListingsWithStatusSchema } from "@redotech/redo-inventory-common/listing-status";
import {
  productFamilyIdFormat,
  productFamilyImageIdFormat,
  productFamilyOptionIdFormat,
  productFamilyOptionValueIdFormat,
  productIdFormat,
  productImageIdFormat,
} from "@redotech/redo-product-id";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

const ImageSchema = z.object({
  id: z.string(),
  url: z.string(),
  altText: z.string(),
  thumbhash: z.string().nullish(),
  position: z.number(),
});

const ProductPriceSchema = z.object({
  productId: productIdFormat.schema,
  price: z.number(),
  currency: z.string(),
});

const ProductCostSchema = z.object({
  productId: productIdFormat.schema,
  cost: z.number(),
  currency: z.string(),
});

const ProductOptionValueInputSchema = z.object({
  id: productFamilyOptionValueIdFormat.schema.optional(),
  value: z.string().min(1),
});

const ProductOptionInputSchema = z.object({
  id: productFamilyOptionIdFormat.schema.optional(),
  name: z.string().min(1),
  values: z.array(ProductOptionValueInputSchema),
});
export type ProductOptionInput = z.infer<typeof ProductOptionInputSchema>;

// Product option selection for creating/updating products
const ProductOptionSelectionInputSchema = z.object({
  optionId: productFamilyOptionIdFormat.schema.optional(),
  valueId: productFamilyOptionValueIdFormat.schema.optional(),
  optionName: z.string(),
  valueName: z.string(),
});

const ProductInputSchema = z.object({
  id: productIdFormat.schema.optional(),
  title: z.string(),
  sku: z.string().optional(),
  upc: z.string().nullable().optional(),
  price: z.number().optional(),
  cost: z.number().optional(),
  currency: z.string(),
  optionValues: z.array(ProductOptionSelectionInputSchema),
});
export type ProductInput = z.infer<typeof ProductInputSchema>;

const ListingImageSelectionSchema = z.object({
  imageId: z.union([
    productFamilyImageIdFormat.schema,
    productImageIdFormat.schema,
  ]),
  source: createEnum(["product_family", "product"]),
  position: z.number(),
});
export type ListingImageSelection = z.infer<typeof ListingImageSelectionSchema>;

export const ListingImagesInputSchema = z.object({
  selectedImages: z.array(ListingImageSelectionSchema),
  isUsingDefaults: z.boolean(),
});
export type ListingImagesInput = z.infer<typeof ListingImagesInputSchema>;

export const updateProductFamily = {
  name: "updateProductFamily" as const,
  input: z.object({
    productFamilyId: productFamilyIdFormat.schema,
    title: z.string().optional(),
    description: z.string().optional(),
    vendor: z.string().nullable().optional(),
    tags: z.array(z.string()).optional(),
    kind: ProductFamilyKindSchema.optional(),
    requiresSerialNumber: z.boolean().optional(),
    attributes: z.array(AttributeSchema).optional(),
    images: z.array(ImageSchema).optional(),
    productPrices: z.array(ProductPriceSchema).optional(),
    productCosts: z.array(ProductCostSchema).optional(),
    productOptions: z.array(ProductOptionInputSchema).optional(),
    listings: ListingsWithStatusSchema,
    products: z.array(ProductInputSchema).optional(),
    productsToDelete: z.array(productIdFormat.schema).optional(),
    listingImages: ListingImagesInputSchema.optional(),
  }),
  output: z.object({ success: z.boolean() }),
};
